import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutlined,
  LabelOutlined,
  MoreVert,
  Replay,
} from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const STATUS_CHIP = {
  open:    { label: "Open",    color: "primary" },
  pending: { label: "Pending", color: "warning" },
  closed:  { label: "Closed",  color: "default" },
};

const TicketActionButtons = ({ ticket }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);
  const [labelLoading, setLabelLoading] = useState(false);
  const [queues, setQueues] = useState([]);
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin" || user?.profile === "superadmin";

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/queue").then(({ data }) => setQueues(data)).catch(toastError);
  }, [isAdmin]);

  const handleLabelChange = async (e) => {
    const queueIds = e.target.value;
    setLabelLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { queueIds });
    } catch (err) {
      toastError(err);
    } finally {
      setLabelLoading(false);
    }
  };

  const handleUpdateStatus = async (status, userId, actionKey) => {
    setLoadingAction(actionKey);
    try {
      await api.put(`/tickets/${ticket.id}`, { status, userId: userId || null });
      if (status === "open") navigate(`/tickets/${ticket.id}`);
      else navigate("/tickets");
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingAction(null);
    }
  };

  const currentLabelIds = ticket.queues?.map(q => q.id) ?? [];
  const chip = STATUS_CHIP[ticket.status] ?? STATUS_CHIP.open;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 1.5,
        py: 0.75,
        gap: 1,
        borderTop: "1px solid",
        borderColor: "divider",
        minHeight: 44,
      }}
    >
      {/* ── Left: label display / admin selector ───────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flex: 1,
          minWidth: 0,
          flexWrap: "wrap",
        }}
      >
        {isAdmin ? (
          /* Admin — multi-select dropdown styled as a compact control */
          <FormControl size="small">
            <Select
              multiple
              value={currentLabelIds}
              onChange={handleLabelChange}
              disabled={labelLoading}
              displayEmpty
              variant="outlined"
              renderValue={(selected) => {
                if (!selected.length)
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.5 }}>
                      <LabelOutlined sx={{ fontSize: 13 }} />
                      <Typography variant="caption">Labels</Typography>
                    </Box>
                  );
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    {selected.map((id) => {
                      const q = queues.find((q) => q.id === id);
                      return q ? (
                        <Box
                          key={id}
                          sx={{ display: "flex", alignItems: "center", gap: 0.4 }}
                        >
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              bgcolor: q.color,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="caption" lineHeight={1}>
                            {q.name}
                          </Typography>
                        </Box>
                      ) : null;
                    })}
                  </Box>
                );
              }}
              sx={{
                height: 28,
                fontSize: 12,
                "& .MuiSelect-select": { py: 0, pl: 1, pr: 2.5 },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
              }}
            >
              {queues.map((q) => (
                <MenuItem key={q.id} value={q.id} dense>
                  <Checkbox
                    size="small"
                    checked={currentLabelIds.includes(q.id)}
                    sx={{ color: q.color, p: "2px 6px 2px 2px", "&.Mui-checked": { color: q.color } }}
                  />
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: q.color, mr: 0.75 }} />
                  <ListItemText
                    primary={q.name}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          /* Member — read-only label chips */
          ticket.queues?.length > 0 ? (
            ticket.queues.map((q) => (
              <Chip
                key={q.id}
                size="small"
                label={q.name}
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  bgcolor: `${q.color}22`,
                  color: q.color,
                  border: `1px solid ${q.color}55`,
                  "& .MuiChip-label": { px: 0.75 },
                }}
                icon={
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: q.color,
                      ml: "6px !important",
                      mr: "-2px !important",
                    }}
                  />
                }
              />
            ))
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LabelOutlined sx={{ fontSize: 13 }} /> No labels
            </Typography>
          )
        )}
      </Box>

      {/* ── Right: status chip + action buttons ─────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
        <Chip
          label={chip.label}
          size="small"
          color={chip.color}
          variant="outlined"
          sx={{
            height: 22,
            fontWeight: 600,
            fontSize: "0.7rem",
            borderRadius: 1.5,
            "& .MuiChip-label": { px: 1 },
          }}
        />

        {ticket.status === "closed" && (
          <ButtonWithSpinner
            loading={loadingAction === "reopen"}
            startIcon={<Replay sx={{ fontSize: "14px !important" }} />}
            size="small"
            variant="outlined"
            onClick={() => handleUpdateStatus("open", user?.id, "reopen")}
            sx={{ textTransform: "none", height: 28, fontSize: "0.78rem" }}
          >
            {i18n.t("messagesList.header.buttons.reopen")}
          </ButtonWithSpinner>
        )}

        {ticket.status === "open" && (
          <>
            <Tooltip title="Return to queue">
              <ButtonWithSpinner
                loading={loadingAction === "return"}
                startIcon={<Replay sx={{ fontSize: "14px !important" }} />}
                size="small"
                variant="outlined"
                onClick={() => handleUpdateStatus("pending", null, "return")}
                sx={{ textTransform: "none", height: 28, fontSize: "0.78rem" }}
              >
                {i18n.t("messagesList.header.buttons.return")}
              </ButtonWithSpinner>
            </Tooltip>
            <ButtonWithSpinner
              loading={loadingAction === "resolve"}
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckCircleOutlined sx={{ fontSize: "14px !important" }} />}
              onClick={() => handleUpdateStatus("closed", user?.id, "resolve")}
              sx={{ textTransform: "none", height: 28, fontSize: "0.78rem" }}
            >
              {i18n.t("messagesList.header.buttons.resolve")}
            </ButtonWithSpinner>
            <IconButton
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ p: 0.5 }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <TicketOptionsMenu
              ticket={ticket}
              anchorEl={anchorEl}
              menuOpen={Boolean(anchorEl)}
              handleClose={() => setAnchorEl(null)}
            />
          </>
        )}

        {ticket.status === "pending" && (
          <ButtonWithSpinner
            loading={loadingAction === "accept"}
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleUpdateStatus("open", user?.id, "accept")}
            sx={{ textTransform: "none", height: 28, fontSize: "0.78rem" }}
          >
            {i18n.t("messagesList.header.buttons.accept")}
          </ButtonWithSpinner>
        )}
      </Box>
    </Box>
  );
};

export default TicketActionButtons;
